import { resolveBackend, resolveEnvironment, resolveHost } from '@packages/contexts/runtime/runtime.helpers';
import { ConfigService, Constants } from '@packages/core/config';
import type { Container } from '@packages/core/container';
import type { LogDestination } from '@packages/core/logging';
import { ConsoleDestination, LogLevel, configureLogger, getLogger, tryParseLogLevel } from '@packages/core/logging';
import { getReleaseVersion } from '@web/config';

import { DatadogLogsDestination } from '../datadog-logs.destination';
import { SessionOverlayStorage } from '../logger-overlay.storage';
import { BadgeConsoleFormatter } from '../pretty-console.formatter';

/**
 * Configure the module logger for the web app: runtime context seed, badged
 * console + optional Datadog destinations, LOG_LEVEL, overlay persistence, and
 * the `window.rootLogger` devtools handle. Must run right after
 * `registerWebPrimitives` — it needs ConfigService, and later registrations
 * log through the configured root.
 */
export function registerWebLogging(container: Container): void {
    const configService = container.resolve(ConfigService);
    const environment = resolveEnvironment(configService.debugEnvName);
    const host = resolveHost(environment, window.location.hostname);
    const backend = resolveBackend(host, configService.get(Constants.Env.ApiBaseUrl, '') ?? '');
    const rootLogger = getLogger();

    rootLogger.setContextLayer('runtime', { backend, environment, host, platform: 'web' });

    const configuredLevel = configService.get(Constants.Env.LogLevel, '') ?? '';
    const fallbackLevel = host === 'local' ? LogLevel.Debug : LogLevel.Warn;
    const level = tryParseLogLevel(configuredLevel) ?? fallbackLevel;

    const destinations: LogDestination[] = [new ConsoleDestination(new BadgeConsoleFormatter())];
    // Datadog ships at LOG_LEVEL even for loggers that run below it (e.g. `http`).
    const datadogDestination = DatadogLogsDestination.createFromConfig(
        configService,
        { env: environment, version: getReleaseVersion() },
        level
    );

    if (datadogDestination) {
        destinations.push(datadogDestination);
    }

    configureLogger({
        destinations,
        level,
        overlayStorage: new SessionOverlayStorage(window.sessionStorage),
    });

    if (configuredLevel !== '' && tryParseLogLevel(configuredLevel) === undefined) {
        rootLogger.warn(`Ignoring unknown LOG_LEVEL [${configuredLevel}], using [${level}]`, {
            configuredLevel,
            fallbackLevel: level,
        });
    }

    window.rootLogger = rootLogger;
}
